import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, FunctionSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import FormulaEditorDialog, { type FormulaEditorPreview } from './FormulaEditorDialog';

/**
 * Invoerveld voor uren: een getal, of een formule die begint met '='.
 *
 * Formules verwijzen naar de code van andere regels, en die codes zijn lang
 * (`seo_10_van_design_dev_uren`). Die uit het hoofd overtypen is precies de
 * manier om de fout te maken die we met benoemde codes juist wilden uitsluiten,
 * dus zodra je binnen een formule typt verschijnt er een lijst met bestaande
 * regels. De uitkomst en een eventuele fout staan onder het veld: een formule
 * waarvan je de uitkomst niet ziet, is een formule die je niet kunt vertrouwen.
 */

export interface FormulaCodeOption {
  code: string;
  description: string;
  kind?: 'manual' | 'formula';
  hours?: number;
  formula?: string;
}

interface FormulaInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Codes van andere regels; de eigen code hoort hier niet in — dat is een kringverwijzing. */
  options: FormulaCodeOption[];
  /** Uitkomst van de formule, uitgerekend door de backend. */
  resolvedHours?: number;
  /** Bedrag van deze regel bij de uitgerekende uren. */
  resolvedAmount?: string;
  error?: string;
  ariaLabel?: string;
  /** Leesbare samenvatting die buiten focus de technische formule vervangt. */
  formulaSummary?: string;
  lineLabel?: string;
  variantName?: string;
  rateLabel?: string;
  /** De prijskaart toont de uitkomst in een eigen kolom. */
  showResult?: boolean;
  /** Rekent een nog niet toegepaste formule door voor de begeleide editor. */
  onPreview?: (formula: string) => Promise<FormulaEditorPreview>;
}

const FUNCTIONS = ['SUM', 'MIN', 'MAX', 'ROUND', 'CEIL', 'FLOOR', 'ABS'];

const isFormula = (value: string) => value.trim().startsWith('=');

/** Het woord waar de cursor in staat; daarop filteren we de suggesties. */
function tokenAtCaret(value: string, caret: number): { token: string; start: number } {
  let start = caret;
  while (start > 0 && /[A-Za-z0-9_]/.test(value[start - 1])) start -= 1;
  return { token: value.slice(start, caret), start };
}

export function FormulaInput({
  value,
  onChange,
  options,
  resolvedHours,
  resolvedAmount,
  error,
  ariaLabel,
  formulaSummary,
  lineLabel,
  variantName,
  rateLabel,
  showResult = true,
  onPreview,
}: FormulaInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [caret, setCaret] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  // Wat de gebruiker typt, zolang het veld focus heeft. De ouder slaat uren als
  // getal op, dus zonder deze tekst wordt '0,' direct weer '0' en kun je geen
  // halve uren intikken.
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? value;

  const formula = isFormula(text);

  const suggestions = useMemo(() => {
    if (!formula || !open) return [];
    const { token } = tokenAtCaret(text, caret);
    if (!token) return [];
    const needle = token.toLowerCase();

    const functionMatches = FUNCTIONS.filter((name) => name.toLowerCase().startsWith(needle)).map(
      (name) => ({ code: name, description: 'functie' })
    );

    const codeMatches = options.filter(
      (option) =>
        option.code.toLowerCase().includes(needle) ||
        option.description.toLowerCase().includes(needle)
    );

    return [...functionMatches, ...codeMatches].slice(0, 8);
  }, [formula, open, options, text, caret]);

  useEffect(() => setHighlight(0), [suggestions.length]);

  const applySuggestion = (code: string) => {
    const input = inputRef.current;
    const position = input?.selectionStart ?? caret;
    const { start } = tokenAtCaret(text, position);
    const insert = FUNCTIONS.includes(code) ? `${code}(` : code;
    const next = text.slice(0, start) + insert + text.slice(position);

    setDraft(next);
    onChange(next);
    setOpen(false);

    // Cursor achter het ingevoegde stuk zetten; anders typt de gebruiker verder
    // aan het begin van het veld.
    window.requestAnimationFrame(() => {
      const target = start + insert.length;
      input?.focus();
      input?.setSelectionRange(target, target);
      setCaret(target);
    });
  };

  const syncCaret = () => setCaret(inputRef.current?.selectionStart ?? 0);

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          aria-label={ariaLabel}
          className={cn(
            'h-8 w-full pr-9',
            formula && 'border-info/70 pl-7 text-xs focus-visible:ring-info/30',
            formula && focused && 'font-mono',
            error && 'border-destructive focus-visible:ring-destructive'
          )}
          value={formula && !focused && formulaSummary ? formulaSummary : text}
          onChange={(event) => {
            setDraft(event.target.value);
            onChange(event.target.value);
            setCaret(event.target.selectionStart ?? 0);
            setOpen(true);
          }}
          onFocus={() => {
            setFocused(true);
            syncCaret();
            setOpen(true);
            window.requestAnimationFrame(() => {
              const target = text.length;
              inputRef.current?.setSelectionRange(target, target);
              setCaret(target);
            });
          }}
          onBlur={() => {
            setFocused(false);
            setDraft(null);
            window.setTimeout(() => setOpen(false), 120);
          }}
          onKeyUp={syncCaret}
          onClick={syncCaret}
          onKeyDown={(event) => {
            if (!suggestions.length) {
              if (event.key === 'Enter') inputRef.current?.blur();
              return;
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setHighlight((current) => (current + 1) % suggestions.length);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlight((current) => (current - 1 + suggestions.length) % suggestions.length);
            } else if (event.key === 'Enter' || event.key === 'Tab') {
              event.preventDefault();
              applySuggestion(suggestions[highlight].code);
            } else if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
        />

        {formula && (
          <FunctionSquare className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
        )}

        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="absolute right-0.5 top-0.5 h-7 w-7 text-muted-foreground hover:text-foreground"
          aria-label={formula ? 'Formule bewerken' : 'Formule maken'}
          title={formula ? 'Formule bewerken' : 'Formule maken'}
          onClick={() => setEditorOpen(true)}
        >
          <FunctionSquare className="h-3.5 w-3.5" />
        </Button>
      </div>

      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-72 overflow-hidden rounded-md border bg-popover shadow-md">
          {suggestions.map((option, index) => (
            <li key={option.code}>
              <button
                type="button"
                tabIndex={-1}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => applySuggestion(option.code)}
                onMouseEnter={() => setHighlight(index)}
                className={cn(
                  'flex w-full flex-col items-start gap-0.5 px-2 py-1.5 text-left',
                  index === highlight && 'bg-accent'
                )}
              >
                <code className="text-xs font-medium">{option.code}</code>
                <span className="w-full truncate text-[11px] text-muted-foreground">
                  {option.description}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {showResult && error ? (
        <p className="mt-0.5 flex items-start gap-1 text-[11px] text-destructive">
          <AlertCircle className="mt-px h-3 w-3 shrink-0" />
          <span>{error}</span>
        </p>
      ) : showResult && formula && resolvedHours !== undefined ? (
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          = {resolvedHours} uur{resolvedAmount ? ` · ${resolvedAmount}` : ''}
        </p>
      ) : null}

      <FormulaEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        value={text}
        options={options}
        lineLabel={lineLabel}
        variantName={variantName}
        rateLabel={rateLabel}
        initialPreview={{ hours: resolvedHours, amount: resolvedAmount, error }}
        onPreview={onPreview}
        onApply={onChange}
      />
    </div>
  );
}

export default FormulaInput;
